import Dashboards from '@highcharts/dashboards/es-modules/masters/dashboards.src.js';
import '@highcharts/dashboards/es-modules/masters/modules/layout.src.js';
import '@highcharts/dashboards/css/dashboards.css';
import Highcharts from 'highcharts';
import { KPI_DEFINITIONS } from './liveDashboardModel';

export const LIVE_DASHBOARD_BOARD_CONTAINER_ID = 'live-dashboard-board';

let dashboardsModulesInitialized = false;

/** Register Dashboards layout + Highcharts plugin once. */
export const ensureLiveDashboardBoardModules = () => {
  if (dashboardsModulesInitialized) {
    return;
  }

  Dashboards.HighchartsPlugin.custom.connectHighcharts(Highcharts);
  Dashboards.PluginHandler.addPlugin(Dashboards.HighchartsPlugin);
  dashboardsModulesInitialized = true;
};

const escapeCsvCell = (value) => {
  const text = value == null ? '' : String(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
};

const buildKpiConnectorCsv = (kpi) => [
  'displayValue,rawValue',
  `${escapeCsvCell(kpi.displayValue)},${escapeCsvCell(kpi.value ?? '')}`,
].join('\n');

const buildBarConnectorCsv = (barChart) => {
  const rows = ['year,value'];
  barChart.barCategories.forEach((year, index) => {
    rows.push(`${escapeCsvCell(year)},${escapeCsvCell(barChart.barData[index] ?? '')}`);
  });
  return rows.join('\n');
};

/**
 * Maps buildLiveDashboardModel() output into Dashboards data-pool connectors
 * (one connector per KPI row + one per historic bar series).
 */
export const buildLiveDashboardDataPool = (dashboardModel) => {
  if (!dashboardModel) {
    return undefined;
  }

  const { kpiCards, barCharts } = dashboardModel;

  return {
    connectors: [
      ...kpiCards.map((kpi) => ({
        id: `live-dashboard-kpi-${kpi.id}`,
        type: 'CSV',
        csv: buildKpiConnectorCsv(kpi),
      })),
      ...barCharts.map((barChart) => ({
        id: `live-dashboard-bar-${barChart.id}`,
        type: 'CSV',
        csv: buildBarConnectorCsv(barChart),
      })),
    ],
  };
};

const buildKpiComponents = (kpiCards) =>
  kpiCards.map((kpi) => ({
    renderTo: `kpi-${kpi.id}`,
    type: 'KPI',
    className: [
      'highcharts-dashboards-component',
      'highcharts-dashboards-component-kpi',
      'live-dashboard-kpi-component',
      `live-dashboard-kpi-tier-${kpi.valueSizeTier}`,
      kpi.id === 9006 ? 'live-dashboard-kpi-long-title' : '',
    ].filter(Boolean).join(' '),
    title: kpi.name,
    value: Number.isFinite(Number(kpi.value)) ? Number(kpi.value) : 0,
    valueFormatter() {
      return kpi.displayValue ?? '—';
    },
    linkedValueTo: {
      enabled: false,
    },
    connector: {
      id: `live-dashboard-kpi-${kpi.id}`,
    },
    columnName: 'displayValue',
    style: {
      backgroundColor: '#2251FF',
      borderColor: '#2251FF',
      color: '#ffffff',
    },
  }));

/**
 * Builds component config for Dashboards.board() using a custom DOM layout
 * (gui disabled so chart header can sit between KPI row and bar chart).
 */
export const buildLiveDashboardBoardConfig = ({
  dashboardModel,
  barChartOptions,
  includeBarChart = true,
}) => {
  if (!dashboardModel) {
    return null;
  }

  const { kpiCards } = dashboardModel;
  const components = buildKpiComponents(kpiCards);

  if (includeBarChart && barChartOptions) {
    components.push({
      renderTo: 'live-dashboard-chart',
      type: 'Highcharts',
      chartOptions: barChartOptions,
    });
  }

  return {
    dataPool: buildLiveDashboardDataPool(dashboardModel),
    gui: {
      enabled: false,
    },
    components,
  };
};

export const KPI_CELL_IDS = KPI_DEFINITIONS.map((definition) => `kpi-${definition.id}`);

export const createLiveDashboardBoard = (containerId, config) => {
  ensureLiveDashboardBoardModules();
  return Dashboards.board(containerId, config);
};

export const destroyLiveDashboardBoard = (board) => {
  if (board?.destroy) {
    board.destroy();
  }
};

/** Resolve the mounted Highcharts instance for image / CSV export. */
export const getBoardHighchartsChart = (board, renderToId = 'live-dashboard-chart') => {
  if (!board) {
    return null;
  }

  const mountedComponents = board.mountedComponents ?? board.components ?? [];
  const match = mountedComponents.find((entry) => {
    const cellId = entry.cell?.id ?? entry.options?.renderTo ?? entry.renderTo;
    return cellId === renderToId;
  });

  return match?.chart ?? match?.chartInstance ?? null;
};

export { Dashboards };
